import { BACKEND, DOCS_FORM_NAME_TO_AI_CORE_FORM_NAME } from "core/consts";
import transport from "core/model/api/transport";
import {
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStartResponse,
  DocsFormStatusResponse,
  ExtractOCRResult,
  ExtractOCRStatusResponse,
  FileCategory,
  FileState,
  FormMetadata,
} from "core/types";

export type DocsSaveValidatedFormPayload<T extends DocsFormName> = {
  form_data: DocsFormDataMapped<T>;
  metadata: FormMetadata<T>;
};

export default {
  docs: {
    fill: async ({
      auctionId,
      careseekerId,
      consultationId,
      formName,
      patientId,
    }: {
      careseekerId: number;
      patientId: number;
      auctionId: number | undefined;
      consultationId: number | undefined;
      formName: DocsFormName;
    }): Promise<DocsFormStatusResponse> => {
      if (!auctionId && !consultationId) {
        throw new Error("auctionId or consultationId is not defined");
      }
      const route = auctionId
        ? `/v1/docs/careseeker/${careseekerId}/patient/${patientId}/auction/${auctionId}/form/${DOCS_FORM_NAME_TO_AI_CORE_FORM_NAME[formName]}/status`
        : `/v1/docs/careseeker/${careseekerId}/patient/${patientId}/consultation/${consultationId}/form/${DOCS_FORM_NAME_TO_AI_CORE_FORM_NAME[formName]}/status`;
      return transport.get<DocsFormStatusResponse>({
        backend: BACKEND.AI_CORE,
        route,
      });
    },
    saveValidatedForm: async <T extends DocsFormName>({
      auctionId,
      careseekerId,
      consultationId,
      formName,
      patientId,
      validatedForm,
    }: {
      careseekerId: number;
      patientId: number;
      auctionId: number | undefined;
      consultationId: number | undefined;
      formName: T;
      validatedForm: DocsSaveValidatedFormPayload<T>;
    }): Promise<DocsFormStartResponse> => {
      if (!auctionId && !consultationId) {
        throw new Error("auctionId or consultationId is not defined");
      }
      const route = auctionId
        ? `/v1/docs/careseeker/${careseekerId}/patient/${patientId}/auction/${auctionId}/form/${DOCS_FORM_NAME_TO_AI_CORE_FORM_NAME[formName]}/save`
        : `/v1/docs/careseeker/${careseekerId}/patient/${patientId}/consultation/${consultationId}/form/${DOCS_FORM_NAME_TO_AI_CORE_FORM_NAME[formName]}/save`;

      return transport.post<DocsFormStartResponse>({
        backend: BACKEND.AI_CORE,
        body: validatedForm,
        route,
      });
    },
    start: async ({
      auctionId,
      careseekerId,
      consultationId,
      externalId,
      formData,
      formName,
      patientId,
    }: {
      careseekerId: number;
      patientId: number;
      auctionId: number | undefined;
      consultationId: number | undefined;
      formName: DocsFormName;
      formData: DocsFormDataMapped<typeof formName> | undefined;
      externalId: string | undefined;
    }): Promise<DocsFormStartResponse> => {
      if (!auctionId && !consultationId) {
        throw new Error("auctionId or consultationId is not defined");
      }
      const route = auctionId
        ? `/v1/docs/careseeker/${careseekerId}/patient/${patientId}/auction/${auctionId}/form/${DOCS_FORM_NAME_TO_AI_CORE_FORM_NAME[formName]}`
        : `/v1/docs/careseeker/${careseekerId}/patient/${patientId}/consultation/${consultationId}/form/${DOCS_FORM_NAME_TO_AI_CORE_FORM_NAME[formName]}`;

      return transport.post<DocsFormStartResponse>({
        backend: BACKEND.AI_CORE,
        body: {
          careseeker_id: careseekerId,
          form_id: DOCS_FORM_NAME_TO_AI_CORE_FORM_NAME[formName],
          patient_id: patientId,
          ...(formData
            ? {
                recare_data: {
                  form: formData,
                  // todo: add meta data like last auction/patient update date
                },
              }
            : {}),
        },
        route: `${route}${externalId ? `?case_id=${externalId}` : ""}`,
      });
    },
  },
  extract: {
    delete: async ({
      careseekerId,
      fileId,
    }: {
      careseekerId?: number;
      fileId: FileState["id"];
    }): Promise<void> => {
      if (!careseekerId) {
        console.error("careseekerId is required to delete an extracted file");
        return;
      }
      return transport.del<void>({
        backend: BACKEND.AI_CORE,
        body: {},
        route: `/v1/extract/${careseekerId}/${fileId}`,
      });
    },
    ocr: async ({
      auctionId,
      careseekerId,
      consultationId,
      file,
      fileCategory,
      fileId,
      patientId,
    }: {
      file: File;
      patientId: number;
      careseekerId: number;
      auctionId: number | undefined;
      consultationId: number | undefined;
      fileId: FileState["id"];
      fileCategory: FileCategory;
    }): Promise<ExtractOCRResult> => {
      if (!auctionId && !consultationId) {
        throw new Error("auctionId or consultationId is not defined");
      }
      const formData = new FormData();

      formData.append("file", file);
      formData.append("careseeker_id", careseekerId.toString());
      formData.append("file_category", String(fileCategory));
      formData.append("recare_file_id", String(fileId));

      const route = auctionId
        ? `/v1/extract/careseeker/${careseekerId}/ocr/patient/${patientId}/auction/${auctionId}/file/${fileId}`
        : `/v1/extract/careseeker/${careseekerId}/ocr/patient/${patientId}/consultation/${consultationId}/file/${fileId}`;
      return transport.post<ExtractOCRResult>({
        backend: BACKEND.AI_CORE,
        body: formData,
        route,
      });
    },
    ocrStatus: async ({
      auctionId,
      careseekerId,
      consultationId,
      externalId,
      patientId,
    }: {
      careseekerId: number;
      patientId: number;
      auctionId: number | undefined;
      consultationId: number | undefined;
      externalId: string | undefined;
    }): Promise<ExtractOCRStatusResponse> => {
      if (!auctionId && !consultationId) {
        throw new Error("auctionId or consultationId is not defined");
      }
      const route = auctionId
        ? `/v1/extract/careseeker/${careseekerId}/ocr/patient/${patientId}/auction/${auctionId}/status`
        : `/v1/extract/careseeker/${careseekerId}/ocr/patient/${patientId}/consultation/${consultationId}/status`;

      return transport.get<ExtractOCRStatusResponse>({
        backend: BACKEND.AI_CORE,
        route: `${route}${externalId ? `?case_id=${externalId}` : ""}`,
      });
    },
  },
};
